import { inject, observer } from 'mobx-react'
import { getSnapshot } from 'mobx-state-tree'
import * as React from 'react'
import { Button } from '../../../../../../shared/components/Button'
import { Headline, HeadlineCSS } from '../../../../../../shared/components/Headlines'
import { RadioButtonControl } from '../../../../../../shared/components/RadioButtonControl'
import { Jaw } from '../../../../../../shared/Constants'
import { breakpoint, styled } from '../../../../../../shared/styled-components'
import { Utils } from '../../../../../../shared/Utils'
import { CalculatorStoreInstance } from '../../../../../../stores/CalculatorStore'
import { Text } from '../../../../../../shared/components/Elements'

interface IProps {
  jaw: Jaw
  store?: CalculatorStoreInstance
  onSwitchToRecommendation?: () => void
}

@inject("store")
@observer
export class JawReplacementConfigView extends React.Component<IProps> {
  // -------------------------------------------------------------------------- HELPERS

  private _getRequirement () { return this.props.store!.offer.getJawRequirement(this.props.jaw) }

  // -------------------------------------------------------------------------- EVENTS

  private _onImplantCountChange (count: number) {
    const requirement = this._getRequirement()
    requirement.setImplantCount(count)
    this.forceUpdate()
  }

  private _onImplantAttachmentTypeChange (implantAttachmentType: 'locator'|'telescope'|'bridge') {
    const requirement = this._getRequirement()
    requirement.setImplantAttachmentType(implantAttachmentType)
    this.forceUpdate()
  }

  // -------------------------------------------------------------------------- RENDERING

  private _getImplantText () {
    const requirement = this._getRequirement()
    switch (requirement.implantCount) {
      case 2:
        return 'Zwei Implantate sind die Mindestversorgung für implantatgetragenen Zahnersatz auf Locatoren im Unterkiefer und nur hier wählbar. Schon hier kann ein deutlich verbesserter Halt gegenüber einer regulären Prothese erzielt werden.'
      case 3:
        return 'Durch eine ausgeglichenere Verteilung des Drucks hat man einen deutlich verbesserten Halt gegenüber einer Versorgung auf zwei Implantaten und der Zahnersatz kann nicht mehr so einfach „kippeln“.'
      case 4:
        return 'Vier Implantate stellen die Empfehlung für einen optimalen Halt im Unterkiefer dar. Sie sorgen für eine sehr gute Abstützung und der Zahnersatz kann nicht mehr kippeln. Im Oberkiefer kann der Zahnersatz gaumenfrei gestaltet werden.'
      case 6:
        return 'Sechs Implantate bieten den besten Halt und das Kaugefühl ähnelt dem eigener Zähne. Bei Teleskopen und der Stegversorgung kann der Zahnersatz in Form einer abnehmbaren Brücke gestaltet werden. Im Oberkiefer ist die Konstruktion außerdem gaumenfrei.'
    }
  }

  private _getImplantTypeText () {
    const requirement = this._getRequirement()
    switch (requirement.implantAttachmentType) {
      case 'locator':
        return 'Durch eine Versorgung mit Druckknopfelementen wird ein deutlich verbesserter Halt im Verhältnis zu konventionellen Prothesen erreicht. Das Preis-Leistungs-Verhältnis von Locatoren ist durch vorgefertigte Bauteile, die keine weitere Anpassung benötigen, sehr gut. Die einzelnen Elemente sind kostengünstig und lassen sich leicht austauschen.'
      case 'telescope':
        return 'Teleskope funktionieren nach dem Doppelkronensystem. Durch die parallelwandigen Teleskope werden sowohl ein hoher Halt als auch eine gute Stabilität erreicht. Diese Versorgung kann als einzige mit den eigenen Zähnen kombiniert werden.'
      case 'bridge':
        return 'Eine Stegversorgung ähnelt einer Teleskopversorgung, ergänzt diese jedoch durch einen Steg, der zwischen den einzelnen Implantaten verläuft. Dieser sorgt für maximalen Halt. Die Stabilität ist mit natürlichen Zähnen vergleichbar und bietet ein hohes Maß an Sicherheit und Kaukomfort.'
    }
  }

  private get _imageFileName () {
    const requirement = this._getRequirement()
    const { jaw } = this.props
    return `${jaw}_jaw_${requirement.implantAttachmentType}_${requirement.implantCount}`
  }

  render (): React.ReactNode {
    const requirement = this._getRequirement()
    return (<Container>
      <BackButton primary small onClick={this.props.onSwitchToRecommendation}>Zurück zur Empfehlung</BackButton>
      <Headline>Ihre Optionen</Headline>
      <OptionsSubHeadlineText>Für einen kompletten Zahnersatz im Unterkiefer stehen Ihnen folgende Optionen zur Auswahl:</OptionsSubHeadlineText>

      <Options>
        <Option>
          <div>
            <OptionHeadline>Art der Implantatversorgung</OptionHeadline>

            <RadioButtons>
              <RadioButton label='Locatoren' checked={requirement.implantAttachmentType === 'locator'} onChange={this._onImplantAttachmentTypeChange.bind(this, 'locator')} />
              <RadioButton label='Teleskope' checked={requirement.implantAttachmentType === 'telescope'} onChange={this._onImplantAttachmentTypeChange.bind(this, 'telescope')} />
              <RadioButton label='Stegkonstruktion' checked={requirement.implantAttachmentType === 'bridge'} onChange={this._onImplantAttachmentTypeChange.bind(this, 'bridge')} />
            </RadioButtons>
          </div>
          <OptionText>{this._getImplantTypeText()}</OptionText>
        </Option>

        <Option>
          <div>
            <OptionHeadline>Anzahl Implantate</OptionHeadline>

            <RadioButtons>
              {requirement.implantAttachmentType === 'locator' && requirement.jaw === 'lower' && [
                <RadioButton label='2 Implantate' checked={requirement.implantCount === 2} onChange={this._onImplantCountChange.bind(this, 2)} key='two' />,
                <RadioButton label='3 Implantate' checked={requirement.implantCount === 3} onChange={this._onImplantCountChange.bind(this, 3)} key='three' />
              ]}
              <RadioButton label='4 Implantate' checked={requirement.implantCount === 4} onChange={this._onImplantCountChange.bind(this, 4)} />
              <RadioButton label='6 Implantate' checked={requirement.implantCount === 6} onChange={this._onImplantCountChange.bind(this, 6)} />
            </RadioButtons>
          </div>
          <RecommendationImage src={Utils.getAssetPath(`/assets/calculator/${this._imageFileName}.png`)}/>
          <OptionText>{this._getImplantText()}</OptionText>
        </Option>
      </Options>
    </Container>)
  }
}

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
`

const OptionsSubHeadlineText = styled(Text)`
  max-width: 350px;
  margin-top: 1em;
`

const Options = styled.div`
  
`

const Option = styled.div`
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  border-bottom: 1px solid ${props => props.theme.borderColor};
  padding: 30px 0; 
  &:last-child {
    border-bottom: none;
  }
  ${breakpoint('tablet')`
    flex-direction: row;
  `}
`

const OptionText = styled(Text)`
  ${breakpoint('tablet')`
    max-width: 200px;
  `}
`

const OptionHeadline = styled.h4`
  ${HeadlineCSS};
  font-size: 20px;
  margin-bottom: 15px;
`

const BackButton = styled(Button)`
  ${breakpoint('tablet')`
    position: absolute;
    top: 0;
    right: 0;
  `}
`

const RadioButtons = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
  ${breakpoint('tablet')`
    margin-bottom: 0;
  `}
`

const RadioButton = styled(RadioButtonControl)`
  margin-bottom: 10px;  
  &:last-child {
    margin-bottom: 0;
  }
`

const RecommendationImage = styled.img`
  width: 200px;
  margin: 0 auto 20px auto;
  ${breakpoint('tablet')`
    margin: 0;
  `}
`
